import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Calendar,
  Truck,
  User,
  MapPin,
  ClipboardList,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  RefreshCw,
  Briefcase,
  Users,
  Navigation,
  MessageCircle,
  Send,
  Check
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_OPTIONS = [
  { value: 'NOT_ASSIGNED', label: 'Not Assigned', column: 'todo' },
  { value: 'PRELOAD', label: 'Preload', column: 'todo' },
  { value: 'CONFIRMED', label: 'Confirmed', column: 'inprogress' },
  { value: 'EN_ROUTE', label: 'En Route', column: 'inprogress' },
  { value: 'ON_SITE', label: 'On Site', column: 'inprogress' },
  { value: 'COMPLETED', label: 'Completed', column: 'completed' },
  { value: 'OUT_OF_SERVICE', label: 'Out of Service', column: 'todo' }
];

export default function DispatchManagementPage() {
  const [selectedDate, setSelectedDate] = useState('');
  
  const [assignments, setAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [units, setUnits] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [mstToday, setMstToday] = useState('');
  const [mstTomorrow, setMstTomorrow] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    date: '',
    job_id: '',
    driver_id: '',
    unit_id: '',
    unit_name: '',
    pups: '',
    quads: '',
    vans: '',
    status: 'NOT_ASSIGNED',
    notes: ''
  });

  useEffect(() => {
    // On mount, fetch MST dates from backend first
    const fetchMSTDates = async () => {
      const token = localStorage.getItem('vaultai_token');
      try {
        const res = await axios.get(`${API}/dispatch/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          const today = res.data.today?.date || '';
          const tomorrow = res.data.tomorrow?.date || '';
          setMstToday(today);
          setMstTomorrow(tomorrow);
          // Default to tomorrow's date
          if (!selectedDate && tomorrow) {
            setSelectedDate(tomorrow);
          }
        }
      } catch (err) {
        console.error('Failed to fetch MST dates:', err);
        // Fallback to local calculation
        const now = new Date();
        now.setDate(now.getDate() + 1);
        setSelectedDate(now.toISOString().split('T')[0]);
      }
    };
    
    if (!selectedDate) {
      fetchMSTDates();
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAllData();
    }
  }, [selectedDate]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('vaultai_token');
    
    try {
      // Fetch in parallel
      const [assignmentsRes, jobsRes, driversRes, activeTripsRes] = await Promise.all([
        axios.get(`${API}/dispatch/assignments?date=${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dispatch/jobs?active_only=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dispatch/drivers?active_only=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dispatch/active-trips`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (assignmentsRes.data.success) {
        setAssignments(assignmentsRes.data.assignments || []);
      }
      if (jobsRes.data.success) {
        setJobs(jobsRes.data.jobs || []);
      }
      if (driversRes.data.success) {
        setDrivers(driversRes.data.drivers || []);
      }
      if (activeTripsRes.data.success) {
        setActiveTrips(activeTripsRes.data.active_trips || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUnits = async () => {
    setLoadingUnits(true);
    const token = localStorage.getItem('vaultai_token');
    
    try {
      const response = await axios.get(`${API}/fleetio/vehicles?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUnits(response.data.vehicles || []);
      }
    } catch (error) {
      console.error('Failed to fetch units:', error);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllData();
  };

  const navigateDate = (direction) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + direction);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('vaultai_token');
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        date: selectedDate
      };
      
      if (editingAssignment) {
        await axios.put(`${API}/dispatch/assignments/${editingAssignment.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Assignment updated');
      } else {
        await axios.post(`${API}/dispatch/assignments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Assignment created');
      }
      
      setShowDialog(false);
      setEditingAssignment(null);
      resetForm();
      fetchAllData();
    } catch (error) {
      console.error('Failed to save assignment:', error);
      toast.error('Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assignment) => {
    // Remove confirm dialog - delete directly
    const token = localStorage.getItem('vaultai_token');
    try {
      await axios.delete(`${API}/dispatch/assignments/${assignment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assignment deleted');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const sendToDriver = async (assignment) => {
    if (!assignment.driver) {
      toast.error('No driver assigned');
      return;
    }
    if (!assignment.driver.phone) {
      toast.error('Driver has no phone number');
      return;
    }
    
    setSendingWhatsApp(prev => ({ ...prev, [assignment.id]: true }));
    const token = localStorage.getItem('vaultai_token');
    
    try {
      const response = await axios.post(
        `${API}/dispatch/assignments/${assignment.id}/send-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchAllData(); // Refresh to show sent status
      } else {
        toast.error(response.data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Failed to send WhatsApp:', error);
      toast.error('Failed to send WhatsApp message');
    } finally {
      setSendingWhatsApp(prev => ({ ...prev, [assignment.id]: false }));
    }
  };

  const openNew = () => {
    setEditingAssignment(null);
    resetForm();
    setFormData(prev => ({ ...prev, date: selectedDate }));
    fetchUnits(); // Fetch units from Fleetio when opening dialog
    setShowDialog(true);
  };

  const openEdit = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      date: assignment.date,
      job_id: assignment.job_id || '',
      driver_id: assignment.driver_id || '',
      unit_id: assignment.unit_id || '',
      unit_name: assignment.unit_name || '',
      pups: assignment.pups || '',
      quads: assignment.quads || '',
      vans: assignment.vans || '',
      status: assignment.status || 'NOT_ASSIGNED',
      notes: assignment.notes || ''
    });
    fetchUnits();
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      date: selectedDate,
      job_id: '',
      driver_id: '',
      unit_id: '',
      unit_name: '',
      pups: '',
      quads: '',
      vans: '',
      status: 'NOT_ASSIGNED',
      notes: ''
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr) => {
    return dateStr === mstToday;
  };

  const isTomorrow = (dateStr) => {
    return dateStr === mstTomorrow;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dispatch-management-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-purple-600" />
            Dispatch Management
          </h1>
          <p className="text-slate-500 text-sm">Create and manage daily dispatch assignments</p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/dispatch/jobs">
            <Button variant="outline" size="sm">
              <Briefcase className="h-4 w-4 mr-2" />
              Jobs ({jobs.length})
            </Button>
          </Link>
          <Link to="/dashboard/dispatch/drivers">
            <Button variant="outline" size="sm">
              <Users className="h-4 w-4 mr-2" />
              Drivers ({drivers.length})
            </Button>
          </Link>
        </div>
      </div>

      {/* Date Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-slate-500" />
              <div className="text-center">
                <div className="text-lg font-semibold">{formatDate(selectedDate)}</div>
                <div className="text-xs text-slate-500">
                  {isToday(selectedDate) ? 'Today' : isTomorrow(selectedDate) ? 'Tomorrow' : ''}
                </div>
              </div>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigateDate(1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Trips Widget - Only show for today's date */}
      {activeTrips.length > 0 && isToday(selectedDate) && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Navigation className="h-5 w-5 text-green-500" />
              Active Trips ({activeTrips.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {activeTrips.map((trip) => (
                <div key={trip.device_id} className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 mb-1">
                    <Truck className="h-4 w-4 text-green-500" />
                    <span className="font-medium">{trip.unit_name}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {trip.departed_from && `Left ${trip.departed_from}`}
                  </div>
                  {trip.job && (
                    <div className="text-xs text-blue-500 mt-1">
                      Job: {trip.job.client_name}
                    </div>
                  )}
                  {trip.driver && (
                    <div className="text-xs text-slate-500">
                      Driver: {trip.driver.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dispatch Assignments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Assignments for {formatDate(selectedDate)}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={openNew} data-testid="add-assignment-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Assignment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-4">No assignments for this date</p>
              <Button onClick={openNew}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Assignment
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {assignments.map((assignment) => (
                <div 
                  key={assignment.id}
                  className="p-4 border rounded-lg hover:shadow-sm transition-shadow"
                  data-testid={`assignment-${assignment.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 grid gap-3 md:grid-cols-4">
                      {/* Job Info */}
                      <div>
                        <div className="text-xs text-slate-500 mb-1">JOB</div>
                        {assignment.job ? (
                          <div>
                            <div className="font-medium text-blue-600">
                              #{assignment.job.job_number}
                            </div>
                            <div className="text-sm">{assignment.job.client_name}</div>
                            {assignment.job.site_name && (
                              <div className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {assignment.job.site_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">Not assigned</span>
                        )}
                      </div>
                      
                      {/* Driver Info */}
                      <div>
                        <div className="text-xs text-slate-500 mb-1">DRIVER</div>
                        {assignment.driver ? (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-green-600" />
                            <span className="font-medium">{assignment.driver.name}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">Not assigned</span>
                        )}
                      </div>
                      
                      {/* Unit Info */}
                      <div>
                        <div className="text-xs text-slate-500 mb-1">UNIT</div>
                        {assignment.unit_name ? (
                          <div>
                            <div className="flex items-center gap-2">
                              <Truck className={`h-4 w-4 ${assignment.gps?.status === 'moving' ? 'text-green-500' : 'text-blue-600'}`} />
                              <span className="font-medium">{assignment.unit_name}</span>
                            </div>
                            {/* Live GPS Status */}
                            {assignment.gps && (
                              <div className="mt-1">
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    assignment.gps.status === 'moving' 
                                      ? 'bg-green-500/20 text-green-500 border-green-500/30' 
                                      : assignment.gps.status === 'idle'
                                      ? 'bg-amber-500/20 text-amber-500 border-amber-500/30'
                                      : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                                  }`}
                                >
                                  {assignment.gps.status === 'moving' ? '🟢' : assignment.gps.status === 'idle' ? '🟡' : '⚫'} 
                                  {assignment.gps.status.toUpperCase()}
                                  {assignment.gps.status === 'moving' && ` ${Math.round(assignment.gps.speed_kph)} km/h`}
                                </Badge>
                                {assignment.gps.landmark && (
                                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {assignment.gps.landmark}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">Not assigned</span>
                        )}
                        {(assignment.pups || assignment.quads || assignment.vans) && (
                          <div className="text-xs text-slate-500 mt-1">
                            {assignment.pups && `Pups: ${assignment.pups} `}
                            {assignment.quads && `Quads: ${assignment.quads} `}
                            {assignment.vans && `Vans: ${assignment.vans}`}
                          </div>
                        )}
                      </div>
                      
                      {/* Status & Notes */}
                      <div>
                        <div className="text-xs text-slate-500 mb-1">STATUS</div>
                        {assignment.status && assignment.status !== 'NONE' ? (
                          <Badge variant={
                            assignment.status === 'COMPLETED' ? 'success' :
                            assignment.status === 'EN_ROUTE' || assignment.status === 'ON_SITE' ? 'default' :
                            assignment.status === 'OUT_OF_SERVICE' ? 'destructive' : 'secondary'
                          }>
                            {assignment.status.replace('_', ' ')}
                          </Badge>
                        ) : (
                          <Badge variant="outline">No Status</Badge>
                        )}
                        {assignment.notes && (
                          <div className="text-xs text-slate-500 mt-2 line-clamp-2">
                            {assignment.notes}
                          </div>
                        )}
                        {assignment.whatsapp_sent && (
                          <div className="text-xs text-green-500 mt-1 flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Sent to driver
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => sendToDriver(assignment)}
                        disabled={sendingWhatsApp[assignment.id] || !assignment.driver?.phone}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        title={!assignment.driver?.phone ? "Driver has no phone number" : "Send dispatch to driver via WhatsApp"}
                      >
                        {sendingWhatsApp[assignment.id] ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(assignment)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(assignment)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAssignment ? 'Edit Assignment' : 'New Dispatch Assignment'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Job Selection */}
            <div>
              <label className="text-sm font-medium mb-1 block">Job *</label>
              <Select
                value={formData.job_id}
                onValueChange={(value) => setFormData({...formData, job_id: value})}
              >
                <SelectTrigger data-testid="job-select">
                  <SelectValue placeholder="Select a job..." />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Dedup by job_number (matches Jobs page) so jobs with multiple product-type docs don't double-up
                    const seen = new Set();
                    return jobs
                      .filter(j => {
                        const key = j.job_number;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      })
                      .map(job => (
                        <SelectItem key={job.id} value={job.id}>
                          #{job.job_number} - {job.client_name}
                          {job.site_name && ` (${job.site_name})`}
                        </SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
              {formData.job_id && (
                <div className="mt-2 p-3 bg-slate-800 border border-slate-700 rounded text-sm">
                  {(() => {
                    const job = jobs.find(j => j.id === formData.job_id);
                    if (!job) return null;
                    return (
                      <div className="space-y-2 text-slate-200">
                        {job.contact_name && <div><span className="font-semibold text-blue-400">Contact:</span> {job.contact_name} {job.contact_phone && `(${job.contact_phone})`}</div>}
                        {job.directions && <div><span className="font-semibold text-blue-400">Directions:</span> {job.directions}</div>}
                        {job.instructions && <div><span className="font-semibold text-blue-400">Instructions:</span> {job.instructions}</div>}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Driver Selection */}
            <div>
              <label className="text-sm font-medium mb-1 block">Driver *</label>
              <Select
                value={formData.driver_id}
                onValueChange={(value) => setFormData({...formData, driver_id: value})}
              >
                <SelectTrigger data-testid="driver-select">
                  <SelectValue placeholder="Select a driver..." />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name}
                      {driver.phone && ` - ${driver.phone}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Unit Selection (from Fleetio) */}
            <div>
              <label className="text-sm font-medium mb-1 block">Unit (from Fleetio)</label>
              {loadingUnits ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading units from Fleetio...
                </div>
              ) : (
                <Select
                  value={formData.unit_id}
                  onValueChange={(value) => {
                    const unit = units.find(u => u.id?.toString() === value);
                    setFormData({
                      ...formData, 
                      unit_id: value,
                      unit_name: unit?.name || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="unit-select">
                    <SelectValue placeholder="Select a unit..." />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id?.toString()}>
                        {unit.name}
                        {unit.license_plate && ` - ${unit.license_plate}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-slate-500 mt-1">Or enter manually:</p>
              <Input
                placeholder="Unit name/number"
                value={formData.unit_name}
                onChange={(e) => setFormData({...formData, unit_name: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Equipment */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Pups</label>
                <Input
                  placeholder="e.g., 2"
                  value={formData.pups}
                  onChange={(e) => setFormData({...formData, pups: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Quads</label>
                <Input
                  placeholder="e.g., 1"
                  value={formData.quads}
                  onChange={(e) => setFormData({...formData, quads: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Vans</label>
                <Input
                  placeholder="e.g., 0"
                  value={formData.vans}
                  onChange={(e) => setFormData({...formData, vans: e.target.value})}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({...formData, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !formData.job_id || !formData.driver_id}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingAssignment ? 'Save Changes' : 'Create Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
